import React, { useState } from 'react';
import { Lock, Unlock, AlertCircle } from 'lucide-react';
import { Button } from './Button';
import { useJobLock } from '../../hooks/useJobLock';
import type { JobLockData } from '../../utils/types';
import { getLockMetadata } from '../../utils/lockHelpers';

export interface JobLockManagerProps {
  jobId: string;
  currentLock: JobLockData | null | undefined;
  onLockUpdate?: (lockData: JobLockData) => void;
  className?: string;
}

/**
 * Reusable component for managing job lock/unlock
 * 
 * Similar to JobDatesManager - can be used in Job Detail page or anywhere else.
 * Provides complete UI for locking/unlocking jobs with reason input.
 * 
 * @example
 * ```typescript
 * <JobLockManager
 *   jobId={job.id}
 *   currentLock={job.lock_data}
 *   onLockUpdate={(lockData) => console.log('Lock updated:', lockData)}
 * />
 * ```
 */
export const JobLockManager: React.FC<JobLockManagerProps> = ({
  jobId,
  currentLock,
  onLockUpdate,
  className = ''
}) => {
  const { isLocked, lockData, isLoading, lockJob, unlockJob } = useJobLock({
    jobId,
    currentLock,
    onLockUpdate
  });

  const [lockReason, setLockReason] = useState('');
  const [showReasonInput, setShowReasonInput] = useState(false);

  const lockMetadata = getLockMetadata(lockData);

  const handleLockClick = () => {
    if (!isLocked) {
      // Show reason input
      setShowReasonInput(true);
    } else {
      // Unlock directly
      handleUnlock();
    }
  };

  const handleLock = async () => {
    if (!lockReason.trim()) {
      return; // Button should be disabled, but double-check
    }

    try {
      await lockJob(lockReason);
      setLockReason('');
      setShowReasonInput(false);
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleUnlock = async () => {
    try {
      await unlockJob();
      setShowReasonInput(false);
      setLockReason('');
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleCancel = () => {
    setShowReasonInput(false);
    setLockReason('');
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Current Lock Status */}
      {isLocked && lockMetadata && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
          <div className="flex items-start gap-2">
            <Lock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-amber-900 mb-1">
                Job is Locked
              </div>
              <div className="text-xs text-amber-800 space-y-1">
                <div className="break-words">
                  <span className="font-medium">Reason:</span> {lockMetadata.reason}
                </div>
                <div>
                  <span className="font-medium">By:</span> {lockMetadata.lockedBy} • {lockMetadata.lockedAt}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lock/Unlock Controls */}
      <div className="space-y-3">
        {!showReasonInput ? (
          // Toggle Button
          <div className="flex items-center gap-3">
            <Button
              variant={isLocked ? 'secondary' : 'primary'}
              size="sm"
              onClick={handleLockClick}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              {isLocked ? (
                <>
                  <Unlock className="w-4 h-4" />
                  Unlock Job
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  Lock Job
                </>
              )}
            </Button>
            
            {!isLocked && (
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <AlertCircle className="w-4 h-4" />
                <span>Locking prevents date changes and calendar moves</span>
              </div>
            )}
          </div>
        ) : (
          // Lock Reason Input
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Reason for Locking <span className="text-red-500">*</span>
            </label>
            <textarea
              className="form-input w-full"
              rows={3}
              placeholder="Enter reason for locking this job (e.g., 'Customer confirmed specific delivery time')"
              value={lockReason}
              onChange={(e) => setLockReason(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleLock}
                disabled={!lockReason.trim() || isLoading}
              >
                Lock Job
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Info Message */}
      {!isLocked && !showReasonInput && (
        <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
          <strong>What happens when locked:</strong>
          <ul className="list-disc list-inside mt-1 space-y-0.5">
            <li>Cannot drag & drop on calendar</li>
            <li>Cannot change dates in production pages</li>
            <li>Cannot modify dates in production line</li>
          </ul>
        </div>
      )}
    </div>
  );
};



